// ===========================================
// Google Drive Files Admin Page
// ===========================================
// Admin page for managing all synced Google Drive files

import { useState } from 'react';
import { Box, Dialog, DialogContent, Tabs, Tab } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FileList from '../../components/googleDrive/FileList';
import DriveFileUploader from '../../components/googleDrive/DriveFileUploader';
import FileMetadataEditor from '../../components/googleDrive/FileMetadataEditor';
import { useDeleteDriveFile, useGoogleDriveAuthStatus } from '../../hooks/useGoogleDrive';
import { GoogleDriveFile } from '../../api/googleDrive.api';

// ===========================================
// COMPONENT
// ===========================================

export default function GoogleDriveFilesPage() {
  const schoolSlug = extractSchoolSlugFromPath(window.location.pathname);
  const { can } = usePermissions();
  // Upload, edit and delete need integrations.write (roles plan 5.6).
  const canManage = can('integrations', 'write');

  // State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<GoogleDriveFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<GoogleDriveFile | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Queries
  const { data: authStatus } = useGoogleDriveAuthStatus();
  const isConnected = authStatus?.isConnected ?? false;

  // Mutations
  const deleteMutation = useDeleteDriveFile();

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    await deleteMutation.mutateAsync(fileToDelete.id);
    setFileToDelete(null);
  };

  const adminPath = schoolSlug ? `/${schoolSlug}/admin` : '/admin';
  const googleDrivePath = schoolSlug ? `/${schoolSlug}/admin/google-drive` : '/admin/google-drive';

  return (
    <Box>
      <PageHeader
        title="Drive Files"
        subtitle="Manage files synced from Google Drive"
        breadcrumbs={[
          { label: 'Admin', path: adminPath },
          { label: 'Google Drive', path: googleDrivePath },
          { label: 'Files' },
        ]}
        actionLabel={isConnected && canManage ? 'Upload File' : undefined}
        actionIcon={<UploadIcon />}
        onAction={() => setUploadDialogOpen(true)}
      />

      {/* Tabs for filtering */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Files" />
          <Tab label="By Lesson" />
          <Tab label="By Student" />
        </Tabs>
      </Box>

      {/* File List */}
      <FileList
        showFilters={true}
        editable={canManage}
        onEdit={(file) => setEditingFile(file)}
        onDelete={(file) => setFileToDelete(file)}
      />

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <DriveFileUploader
            onUploaded={() => setUploadDialogOpen(false)}
            onClose={() => setUploadDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Metadata Dialog */}
      <FileMetadataEditor
        open={!!editingFile}
        file={editingFile}
        onClose={() => setEditingFile(null)}
        onSuccess={() => setEditingFile(null)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!fileToDelete}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.fileName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setFileToDelete(null)}
      />
    </Box>
  );
}
